import classNames from "classnames";
import join from "lodash/join";
import React, { FunctionComponent, useCallback, useEffect } from "react";
import { FormattedMessage } from "react-intl";
import useCustomizationContext from "../../../hooks/useCustomizationContext";
import { ReactComponent as IconAlertCircle } from "../../../icons/alert-circle.svg";
import { ReactComponent as IconAlertTriangle } from "../../../icons/alert-triangle.svg";
import { ReactComponent as IconCheck } from "../../../icons/check.svg";
import { ReactComponent as IconX } from "../../../icons/x.svg";
import { getNotificationData } from "../../../notifications";
import { NotificationByCode } from "../../../types/notification";
import "./style.scss";

interface Props {
  notification: NotificationByCode;
}

const Notification: FunctionComponent<Props> = ({ notification }) => {
  const { dispatch } = useCustomizationContext();

  const { code, customMessage, key } = notification;
  const { autoClose, type, undoable } = getNotificationData(code);

  const close = useCallback(
    () => dispatch({ type: "removeNotification", notificationKey: key }),
    [dispatch, key]
  );

  const autoCloseDelay = calcAutoCloseDelay(autoClose, undoable);

  useEffect(() => {
    if (autoCloseDelay <= 0) {
      return;
    }

    const timeoutID = setTimeout(() => {
      dispatch({ type: "removeNotification", notificationKey: key });
    }, autoCloseDelay);

    return () => clearTimeout(timeoutID);
  }, [autoCloseDelay, close, dispatch, key]);

  let undoLink = null;
  if (undoable) {
    const onUndoClick = () => {
      dispatch({ type: "undoRedo", way: "undo" });
      close();
    };
    undoLink = (
      <span className="Notification-undo" onClick={onUndoClick}>
        <FormattedMessage id="kustom_undo" />
      </span>
    );
  }

  const codes = [code];

  let Icon;
  switch (type) {
    case "error":
      Icon = IconAlertCircle;
      break;
    case "warning":
      Icon = IconAlertTriangle;
      break;
    case "validation":
      Icon = IconCheck;
      break;
    default: {
      const exhaustiveCheck: never = type;
      throw new Error(
        `Unsupported notification type ${(exhaustiveCheck as { type: string }).type}`
      );
    }
  }

  return (
    <div className={classNames("Notification", `Notification--${type}`)}>
      <div className="Notification-icon" title={join(codes, " ")}>
        <Icon />
      </div>

      <div className="Notification-message">
        {customMessage ? (
          <FormattedMessage id={customMessage.translationId} values={customMessage.parameters} />
        ) : (
          <FormattedMessage id={`kustom_notif_${code}`} />
        )}
        &nbsp;{undoLink}
      </div>

      <div className="Notification-close" onClick={close}>
        <IconX />
      </div>
    </div>
  );
};

export default Notification;

function calcAutoCloseDelay(autoClose: boolean | undefined, undoable: boolean | undefined): number {
  if (!autoClose) {
    return -1;
  }

  return undoable ? 5000 : 2000;
}
